import { Button } from "@octopusdeploy/design-system-components";
import { useMutation } from "@octopusdeploy/octopus-react-client";
import type { MachineResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import React from "react";
import type { AnalyticKubernetesAgentSetupDispatcher } from "~/analytics/Analytics";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PagedOnboardingDialog, type PagedOnboardingDialogPageData } from "~/components/OnboardingDialog/PagedOnboardingDialog";
import { DefaultPermissionsHelpContent, defaultPermissionsHelpImage, OctopusPermissionsControllerHelpContent, octopusPermissionsControllerHelpImage } from "../ConfigurationDialogHelp";
import { DefaultPermissionsPage } from "./DefaultPermissionsPage";
import { OctopusPermissionsControllerInstallationPage } from "./OctopusPermissionsControllerInstallationPage";
interface OctopusPermissionsControllerInstallDialogProps {
    dispatchAction: AnalyticKubernetesAgentSetupDispatcher;
    machine: MachineResource;
    open: boolean;
    closeDialog: () => void;
    isUpgrading?: boolean;
}
export function OctopusPermissionsControllerInstallDialog({ dispatchAction, machine, open, closeDialog, isUpgrading }: OctopusPermissionsControllerInstallDialogProps) {
    const { navigate } = useSpaceAwareNavigation();
    const { execute: createHealthCheckTask, isExecuting: isCreatingHealthCheckTask } = useMutation({
        name: "Create health check task",
        action: (repo) => repo.Tasks.createHealthCheckTaskForMachine(machine),
        afterComplete: async (_, createdTask) => navigate(links.taskPage.generateUrl({ taskId: createdTask.Id })),
    });
    const title = isUpgrading ? "Upgrade Octopus Permissions Controller" : "Install Octopus Permissions Controller";
    const pages: PagedOnboardingDialogPageData[] = [
        {
            name: title,
            title: title,
            progressTrackerLabel: "Permissions",
            helpPanelContent: <OctopusPermissionsControllerHelpContent />,
            helpPanelImage: octopusPermissionsControllerHelpImage(),
            hideCancelButton: isUpgrading,
            onMovingToNextPage: async () => {
                dispatchAction("Submit Standalone Octopus Permissions Controller Screen", {});
            },
            render: () => <OctopusPermissionsControllerInstallationPage isUpgrading={isUpgrading}/>,
            customPrimaryButton: isUpgrading ? <Button size="medium" importance="primary" onClick={createHealthCheckTask} label="Run health check" disabled={isCreatingHealthCheckTask}/> : null,
        },
    ];
    if (!isUpgrading) {
        pages.push({
            name: "Default Permissions for Kubernetes Agent Deployments",
            title: "Default Permissions for Kubernetes Agent Deployments",
            helpPanelContent: <DefaultPermissionsHelpContent />,
            helpPanelImage: defaultPermissionsHelpImage(),
            onMovingToNextPage: async () => {
                dispatchAction("Submit Standalone Default Permissions Screen", {});
            },
            render: () => <DefaultPermissionsPage configurePermissions={false}/>,
            customPrimaryButton: <Button size="medium" importance="primary" onClick={createHealthCheckTask} label="Run health check" disabled={isCreatingHealthCheckTask}/>,
            hideCancelButton: true,
            hideBackButton: true,
        });
    }
    return <PagedOnboardingDialog open={open} name={"Add Octopus Permissions Controller"} contentPanelHeight={600} pages={pages} close={closeDialog} hidePreviousButton={false} hideCompleteButton={false}/>;
}
